import React, { Suspense, lazy } from "react";
import styled, { css } from "styled-components";
import {
  Route,
  Switch,
  useLocation,
  useHistory,
  useParams
} from "react-router-dom";
import { frontEnd } from "../../config/links";
import { CrossIcon } from "../../components/styled-components/icons";
import { TextLink } from "../../components/styled-components/link-text";
import { MQ } from "../../styles/styles-variables";

//router
// import Login from "../../pages/authorization/login";
//import Register from "../../pages/authorization/register";
//import RegisterSuccess from "../../pages/authorization/register-success";

//assets
// import ClosedForAlpha from "../authorization/closed-for-alpha";
//import ConfirmEmail from "../authorization/confirm-email";
// import ForgotPassword from "../authorization/forgot-password";
// import ResendConfirmEmail from "../authorization/resend-confirm-email";
//import ResetPassword from "../authorization/reset-password";

const ResendConfirmEmail = lazy(() =>
  import("../authorization/resend-confirm-email")
);
const ForgotPassword = lazy(() => import("../authorization/forgot-password"));
const ClosedForAlpha = lazy(() => import("../authorization/closed-for-alpha"));
const ResetPassword = lazy(() => import("../authorization/reset-password"));
const ConfirmEmail = lazy(() => import("../authorization/confirm-email"));
const Login = lazy(() => import("../../pages/authorization/login"));
const Register = lazy(() => import("../../pages/authorization/register"));
const RegisterSuccess = lazy(() =>
  import("../../pages/authorization/register-success")
);

interface AuthContainerProps {
  closeHandler?: () => void; // Optional prop for custom close handling
}

// Create a wrapper component to handle both direct and exam-prefixed auth routes
const AuthRouteComponent: React.FC<{ component: React.ComponentType<any> }> = ({
  component: Component
}) => {
  const { examType } = useParams<{ examType?: string }>();

  // Pass the examType as a prop to the auth component
  return <Component examType={examType} />;
};

const AuthContainer: React.FC<AuthContainerProps> = ({ closeHandler }) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const padding =
    pathname !== frontEnd.registerSuccess &&
    !pathname.endsWith(frontEnd.registerSuccess)
      ? "18px 84px"
      : "18px 50px";
  const node = React.useRef<any>();

  const handleClose = () => {
    if (closeHandler) {
      closeHandler();
    } else {
      // If we're on an exam-specific page, go back to that exam page
      const examMatch = pathname.match(/^\/([^\/]+)\//);
      if (
        examMatch &&
        ["bps", "gsse", "gpepau", "gpepnz", "racgp"].includes(examMatch[1])
      ) {
        history.push(`/${examMatch[1]}`);
      } else {
        history.push("/");
      }
    }
  };

  return (
    <Container
      className="auth-container"
      active={pathname !== "/"}
      padding={padding}
      ref={node}
    >
      <Suspense fallback={<div></div>}>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <TextLink
            to=""
            onClick={e => {
              e.preventDefault();
              handleClose();
            }}
          >
            <CrossIcon style={{ width: 12, height: 12, marginRight: 5 }} />
            Close
          </TextLink>
        </div>
        <Switch>
          {/* Standard auth routes */}
          <Route path={frontEnd.login} exact component={Login} />
          <Route path={frontEnd.register} exact component={Register} />
          <Route
            path={frontEnd.registerSuccess}
            exact
            component={RegisterSuccess}
          />
          <Route
            path={frontEnd.forgotPassword}
            exact
            component={ForgotPassword}
          />
          <Route path={frontEnd.confirmEmail} exact component={ConfirmEmail} />
          <Route
            path={frontEnd.resendConfirmEmail}
            exact
            component={ResendConfirmEmail}
          />
          <Route
            path={frontEnd.resetPassword}
            exact
            component={ResetPassword}
          />
          <Route
            path={frontEnd.closedForAlpha}
            exact
            component={ClosedForAlpha}
          />

          {/* Exam-specific auth routes */}
          <Route
            path="/:examType/login/index.html"
            render={props => (
              <AuthRouteComponent component={Login} {...props} />
            )}
          />
          <Route
            path="/:examType/register/index.html"
            render={props => (
              <AuthRouteComponent component={Register} {...props} />
            )}
          />
          <Route
            path="/:examType/register-success"
            render={props => (
              <AuthRouteComponent component={RegisterSuccess} {...props} />
            )}
          />
          <Route
            path="/:examType/confirm-email/index.html"
            render={props => (
              <AuthRouteComponent component={ConfirmEmail} {...props} />
            )}
          />
          <Route
            path="/:examType/resend-confirm-email"
            render={props => (
              <AuthRouteComponent component={ResendConfirmEmail} {...props} />
            )}
          />
          <Route
            path="/:examType/forgot-password"
            render={props => (
              <AuthRouteComponent component={ForgotPassword} {...props} />
            )}
          />
          <Route
            path="/:examType/reset-password/index.html"
            render={props => (
              <AuthRouteComponent component={ResetPassword} {...props} />
            )}
          />
          <Route
            path="/:examType/closed-for-alpha"
            render={props => (
              <AuthRouteComponent component={ClosedForAlpha} {...props} />
            )}
          />
        </Switch>
      </Suspense>
    </Container>
  );
};

const Container = styled.div<{ active: boolean; padding: string }>`
  position: absolute;
  height: auto;
  max-height: 800px;
  background-color: white;
  z-index: 900;
  opacity: 0.98;
  border-radius: 0 0 5px 5px;
  transition: all 0.4s ease-in-out;
  overflow-y: auto;
  overflow-x: hidden;

  ${p =>
    p.active &&
    css`
      transform: translateY(100%);
    `}

  padding: 18px 30px;

  @media ${MQ.mobileL} {
    padding: ${p => p.padding};
  }
`;

export default AuthContainer;
